<template>
  <div class="flex flex-col">
    <div class="flex flex-col">
      <div class="flex items-center justify-between">
        <h3
          v-if="heading"
          :class="headingClasses"
        >
          {{ heading }}
        </h3>
        <div
          v-if="isOverflowed"
          class="hidden md:flex items-center space-x-4"
        >
          <button
            type="button"
            aria-hidden="true"
            :aria-label="$t('search.slide-left')"
            class="text-2xl"
            :class="{ 'cursor-not-allowed opacity-50 pointer-events-none': isOnInitialPosition }"
            @click="slideLeft"
          >
            <fa :icon="['fal', 'circle-chevron-left']" />
          </button>
          <button
            type="button"
            aria-hidden="true"
            :aria-label="$t('search.slide-right')"
            :class="{ 'cursor-not-allowed opacity-50 pointer-events-none': isOnFinalPosition }"
            class="text-2xl"
            @click="slideRight"
          >
            <fa :icon="['fal', 'circle-chevron-right']" />
          </button>
        </div>
      </div>
      <p
        v-if="subHeading"
        :class="subHeadingClasses"
      >
        {{ subHeading }}
      </p>
    </div>
    <div
      ref="slideContainer"
      :class="containerClasses"
      class="scrollbar-none overflow-x-auto"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleChevronLeft,
  faCircleChevronRight,
} from '@fortawesome/pro-light-svg-icons';
import debounce from 'lodash/debounce';

library.add({
  faCircleChevronLeft,
  faCircleChevronRight,
});

export default {
  name: 'VetsterSlideNavigationWrapper',

  props: {
    containerClasses: {
      type: String,
      default: '',
    },
    heading: {
      type: String,
      default: '',
    },
    headingClasses: {
      type: String,
      default: 'text-2xl font-title text-neutral-900 font-medium lg:font-bold',
    },
    subHeading: {
      type: String,
      default: '',
    },
    subHeadingClasses: {
      type: String,
      default: 'mt-1 text-neutral-500 mb-4',
    },
  },

  data() {
    return {
      isOnInitialPosition: true,
      isOnFinalPosition: false,
      isOverflowed: false,
    };
  },

  computed: {
    debouncedUpdateControls() {
      return debounce(this.updateControls, 200);
    },
  },

  mounted() {
    this.checkOverflow();
    window.addEventListener('resize', this.checkOverflow);
    this.$refs.slideContainer.addEventListener('scroll', this.debouncedUpdateControls);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkOverflow);
    this.$refs.slideContainer.removeEventListener('scroll', this.debouncedUpdateControls);
  },

  methods: {
    checkOverflow() {
      this.$nextTick(() => {
        const { slideContainer } = this.$refs;
        this.isOverflowed = slideContainer.scrollWidth > slideContainer.clientWidth;
      });
    },

    slideRight() {
      const { slideContainer } = this.$refs;

      slideContainer.scrollTo({
        left: slideContainer.scrollLeft + slideContainer.offsetWidth,
        behavior: 'smooth',
      });
    },

    slideLeft() {
      const { slideContainer } = this.$refs;

      slideContainer.scrollTo({
        left: slideContainer.scrollLeft - slideContainer.offsetWidth,
        behavior: 'smooth',
      });
    },

    updateControls() {
      const { slideContainer } = this.$refs;
      this.isOnInitialPosition = slideContainer.scrollLeft === 0;
      this.isOnFinalPosition = slideContainer.scrollLeft === (slideContainer.scrollWidth - slideContainer.clientWidth);
      this.checkOverflow();
    },
  },
};
</script>
